import {
	DataField,
	DiagnosticLevel,
	makeBooleanDataField,
	makeDoubleDataField,
	makeStringDataField,
	SuppressNexus,
	TelemetryEvent,
	TelemetryLogger,
	TelemetrySink,
} from "@microsoft/oteljs";
import { isNOU, scrubContent } from "./../../Utils";
import { App, Browser, Device, Host, Release, Session } from "./Contracts";
import ITelemetryLogger from "./ITelemetryLogger";

export default class OtelTelemetryLogger implements ITelemetryLogger {

	private static EVENT_NAME_DELIMITER = ".";
	private oteljsTelemetryLogger: TelemetryLogger;

	// Office Event Schema data contract variables. 
	// Event, User are not used because they're initialized on the TelemetrySink.
	private oesApp: App;
	private oesSession: Session; // This contract is used for the SDK session in this implementation
	private oesHost: Host;
	private oesRelease: Release;
	private oesBrowser: Browser;
	private oesDevice: Device;

	private namespace: string;
	private sequence: number;

	constructor(ariaTenantId: string, namespace: string, telemetrySink: TelemetrySink, app: App, session: Session,
		host?: Host, release?: Release, browser?: Browser, device?: Device, nexusTenantToken: number = SuppressNexus) {
		if (!ariaTenantId) {
			throw new Error("tenantToken must not be null");
		}
		if (!app) {
			throw new Error("app must not be null");
		}
		if (!telemetrySink) {
			throw new Error("telemetrySink must not be null");
		}
		if (!namespace) {
			throw new Error("namespace must not be null");
		}
		if (!session) {
			throw new Error("session must not be null");
		}

		this.namespace = namespace;
		this.oesApp = app; // static persistent
		this.oesSession = session; // dynamic persistent
		this.oesHost = host; // static persistent
		this.oesRelease = release; // static persistent
		this.oesBrowser = browser; // static persistent
		this.oesDevice = device; // static persistent
		this.sequence = 1; // Sequence starts at 1 not 0

		this.oteljsTelemetryLogger = new TelemetryLogger(undefined, this.getStaticPersistentDataFields(this.oesApp, this.oesHost,
			this.oesRelease, this.oesBrowser, this.oesDevice));
		this.oteljsTelemetryLogger.setTenantToken(namespace, ariaTenantId, nexusTenantToken);

		// Attach the provided sink. This is what will transport the telemetry.
		this.oteljsTelemetryLogger.addSink(telemetrySink);
	}

	public logEvent(eventName: string, customProperties?: { [customField: string]: any }, sampleRate = 1): void {
		if (!eventName) {
			throw new Error("eventName must not be null");
		}

		// Translate FG Events into TelemetryEvents
		const otelTelemetryEvent = this.createOTelTelemetryEvent(eventName, customProperties, sampleRate);

		// Log the events with the oteljs logger.
		this.oteljsTelemetryLogger.sendTelemetryEvent(otelTelemetryEvent);

		this.sequence++;
	}

	public flushQueue(): void {
		this.oteljsTelemetryLogger.flushQueue();
	}

	// This method adds persistent properties that are dynamic and change for every event (such as sequence)
	private addDynamicPersistentProperties(telemetryEvent: TelemetryEvent, sampleRate: number): void {
		// Floodgate session Id
		if (!isNOU(this.oesSession)) {
			telemetryEvent.dataFields.push(makeStringDataField("Session.Id", this.oesSession.getId()));
		}

		// Event sequence number
		if (!isNOU(this.sequence)) {
			telemetryEvent.dataFields.push(makeDoubleDataField("Event.Sequence", this.sequence));
		}

		// Event sample rate
		if (!isNOU(sampleRate)) {
			telemetryEvent.dataFields.push(makeDoubleDataField("Event.SampleRate", sampleRate));
		}
	}

	private createOTelTelemetryEvent(eventName: string, customProperties: { [customField: string]: any }, sampleRate: number): TelemetryEvent {
		const telemetryEvent: TelemetryEvent = {
			eventName: this.namespace + OtelTelemetryLogger.EVENT_NAME_DELIMITER + eventName,
			eventFlags: {
				diagnosticLevel: DiagnosticLevel.RequiredServiceData,
			},
			dataFields: [],
		};

		for (const key in customProperties) {
			if (Object.prototype.hasOwnProperty.call(customProperties, key)) {
				const customProperty = customProperties[key];

				let dataField: DataField = undefined;

				// Map property types to otel function to make the datafield.
				switch (typeof (customProperty)) {
					case "string":
						dataField = makeStringDataField(key, scrubContent(customProperty));
						break;
					case "number":
						dataField = makeDoubleDataField(key, customProperty);
						break;
					case "boolean":
						dataField = makeBooleanDataField(key, customProperty);
						break;
					default:
						// do nothing with other types, break out of loop.
						continue;
				}

				if (!isNOU(dataField)) {
					telemetryEvent.dataFields.push(dataField);
				}
			}
		}

		// Add properties that should always be added to events.
		this.addDynamicPersistentProperties(telemetryEvent, sampleRate);

		return telemetryEvent;
	}

	// function to create persistent data fields which does not change within session
	private getStaticPersistentDataFields(oesApp: App, oesHost: Host, oesRelease: Release,
		oesBrowser: Browser, oesDevice: Device): DataField[] {
		const dataFields: DataField[] = [];

		if (!isNOU(oesApp)) {
			dataFields.push(makeStringDataField("App.Name", oesApp.getName()));
			dataFields.push(makeStringDataField("App.Platform", oesApp.getPlatform()));
			dataFields.push(makeStringDataField("App.Version", oesApp.getVersion()));
		}

		if (!isNOU(oesRelease)) {
			dataFields.push(makeStringDataField("Release.AudienceGroup", oesRelease.getAudienceGroup()));
		}

		if (!isNOU(oesDevice)) {
			dataFields.push(makeStringDataField("Device.Id", oesDevice.getId()));
		}

		if (!isNOU(oesHost)) {
			dataFields.push(makeStringDataField("Host.Id", oesHost.getId()));
			dataFields.push(makeStringDataField("Host.Locale", oesHost.getLocale()));
			dataFields.push(makeStringDataField("Host.SessionId2", oesHost.getSessionId()));
			dataFields.push(makeStringDataField("Host.Version", oesHost.getVersion()));
		}

		if (!isNOU(oesBrowser)) {
			dataFields.push(makeStringDataField("Browser.Name", oesBrowser.getName()));
			dataFields.push(makeStringDataField("Browser.Version", oesBrowser.getVersion()));
		}

		return dataFields;
	}
}
